type Attributes = Record<string, unknown>

export type MFESurface = 'modal' | 'page'

export interface MFERuntimeLogger {
  logSuccess: () => void
  logBuildTime: (isRuntimeEnabled: boolean) => void
  logError: (error: Error) => void
}

/**
 * New Relic's addPageAction only accepts string or number attribute values.
 * Booleans, null/undefined, and objects can cause it to throw internally.
 */
const sanitize = (
  attributes?: Attributes
): Record<string, string | number> | undefined => {
  if (!attributes) return undefined
  const result: Record<string, string | number> = {}
  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === 'string' || typeof value === 'number') {
      result[key] = value
    } else {
      result[key] = String(value)
    }
  })
  return result
}

export const recordCustomEvent = (
  name: string,
  attributes?: Attributes
): void => {
  if (!name) return
  try {
    window.newrelic?.addPageAction?.(name, sanitize(attributes))
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('recordCustomEvent failed', e)
    }
  }
}

const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1)

/**
 * Creates a standardised logger for MFE runtime monitoring.
 * Each MFE surface (page/modal) gets its own logger instance.
 *
 * Usage:
 *   const logger = createMFERuntimeLogger('rewards', 'modal')
 *   logger.logSuccess()
 *   logger.logBuildTime(isRuntimeEnabled)
 *   logger.logError(error)
 */
export const createMFERuntimeLogger = (
  mfe: string,
  surface: MFESurface
): MFERuntimeLogger => {
  const eventName = `${capitalize(mfe)}RuntimeLogging`
  const runtimeEnabledKey = `is${capitalize(mfe)}RuntimeEnabled`
  let hasFiredBuildTime = false
  const getBaseAttributes = () => ({
    mfe,
    surface,
    correlationId: sessionStorage.getItem('correlationId')
  })

  return {
    logSuccess: () => {
      recordCustomEvent(eventName, {
        message: 'Displaying runtime MFE',
        [runtimeEnabledKey]: true,
        ...getBaseAttributes()
      })
    },
    logBuildTime: (isRuntimeEnabled: boolean) => {
      if (hasFiredBuildTime) return
      hasFiredBuildTime = true
      recordCustomEvent(eventName, {
        message: 'Displaying build-time MFE',
        [runtimeEnabledKey]: isRuntimeEnabled,
        ...getBaseAttributes()
      })
    },
    logError: (error: Error) => {
      recordCustomEvent(eventName, {
        message: 'Error displaying runtime MFE',
        error: error?.message,
        errorName: error?.name,
        stack: error?.stack,
        [runtimeEnabledKey]: true,
        ...getBaseAttributes()
      })
    }
  }
}
